// Helpers de cálculo — ESPELHO EXATO de backend/core/costs.py (fonte da verdade).
// Regras: 4 casas em cálculos de ficha; QB => custo 0; conversão desconhecida => fator nulo (não 1!).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const NON_METRIC_UNITS = ["unidades", "un", "und", "dúzia", "duzia", "maço", "maco", "cabeça", "cabeca"];

// Aliases idênticos a core/costs.py::_UNIT_ALIASES
const UNIT_ALIASES = {
  g: "g", gr: "g", grama: "g", gramas: "g",
  kg: "kg", quilo: "kg", quilos: "kg", kilo: "kg",
  mg: "mg", ml: "ml",
  l: "l", lt: "l", litro: "l", litros: "l",
  un: "un", und: "un", unid: "un", unidade: "un", unidades: "un",
};
const MASS = ["mg", "g", "kg"];
// Tabela idêntica a core/costs.py::_UNIT_CONV
const UNIT_CONV = {
  "g|kg": 0.001, "kg|g": 1000, "mg|g": 0.001, "g|mg": 1000,
  "mg|kg": 0.000001, "kg|mg": 1000000, "ml|l": 0.001, "l|ml": 1000,
};

export const round4 = (v) => Math.round((Number(v) || 0) * 10000) / 10000;

export const normalizeUnit = (u) => {
  const raw = (u || "").trim().toLowerCase();
  return UNIT_ALIASES[raw] || raw;
};

// Espelho de convert_qty: retorna null quando a conversão é impossível
// (o backend lança UnitConversionError — aqui o chamador mantém o custo anterior
// e deixa o Gatekeeper do servidor decidir).
export const convertQty = (qty, fromUnit, toUnit, pesoEquivalenteG = null) => {
  const f = normalizeUnit(fromUnit);
  const t = normalizeUnit(toUnit);
  if (f === t) return qty;
  const pesoG = Number(pesoEquivalenteG) || 0;
  if (f === "un" && MASS.includes(t)) {
    if (pesoG > 0) return (qty * pesoG) * (UNIT_CONV[`g|${t}`] ?? (t === "g" ? 1 : null));
    return null;
  }
  if (t === "un" && MASS.includes(f)) {
    if (pesoG > 0) return (qty * (UNIT_CONV[`${f}|g`] ?? (f === "g" ? 1 : null))) / pesoG;
    return null;
  }
  const factor = UNIT_CONV[`${f}|${t}`];
  return factor === undefined ? null : qty * factor;
};

// Espelho de compute_item_cost (4 casas; QB => 0; sem preço => null = mantém anterior)
export const computeItemCost = (recipeIng, dbIng) => {
  if (recipeIng?.is_qb) return 0;
  const qtyP = Number(dbIng?.quantity_purchased) || 0;
  const price = Number(dbIng?.purchase_price) || 0;
  if (qtyP <= 0 || price <= 0) return null;
  const ppu = price / qtyP;
  const qty = Number(recipeIng.quantidade_liquida) || Number(recipeIng.quantidade_bruta) || Number(recipeIng.quantity_used) || 0;
  const pesoG = Number(recipeIng.peso_equivalente_g) || Number(dbIng.peso_equivalente_g) || null;
  const qtyConv = convertQty(qty, recipeIng.unit, dbIng.unit, pesoG);
  if (qtyConv === null) return null;
  return round4(qtyConv * ppu);
};

// Espelho de compute_sheet_totals
export const computeSheetTotals = (ingredients, yieldQuantity) => {
  const total = round4((ingredients || []).filter((i) => !i.is_qb).reduce((s, i) => s + (Number(i.item_cost) || 0), 0));
  const y = Number(yieldQuantity) || 0;
  return { totalCost: total, costPerUnit: y > 0 ? round4(total / y) : 0 };
};

export const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

export const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);
};

// Peso em gramas de um item da ficha (fórmula de padeiro / gramatura)
const ingWeightG = (ing) => {
  const qty = Number(ing.quantidade_liquida) || Number(ing.quantidade_bruta) || Number(ing.quantity_used) || 0;
  const u = normalizeUnit(ing.unit);
  if (u === "kg" || u === "l") return qty * 1000;
  if (u === "g" || u === "ml") return qty;
  if (ing.peso_equivalente_g) return qty * Number(ing.peso_equivalente_g);
  return 0;
};

export const calcGramaturaTotalFromList = (ingredients) => {
  return (ingredients || []).filter((i) => !i.is_qb).reduce((sum, ing) => sum + ingWeightG(ing) / 1000, 0);
};

// Fórmula de padeiro: % do ingrediente sobre o ingrediente base (100%)
// Assinatura original preservada: (ing, listaDeIngredientes, idDoIngredienteBase)
export const calcBakerPercent = (ing, ingredientsList, baseId) => {
  if (!baseId || ing.is_qb) return null;
  const baseIng = (ingredientsList || []).find((i) => i.ingredient_id === baseId);
  if (!baseIng) return null;
  const baseWeight = ingWeightG(baseIng);
  const w = ingWeightG(ing);
  if (baseWeight <= 0 || w <= 0) return null;
  return ((w / baseWeight) * 100).toFixed(1);
};
